import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { 
  X, 
  FileArchive, 
  Building, 
  Eye, 
  User2Icon, 
  AlertTriangle, 
  Check, 
  Ban, 
  MessageSquare,
  ArrowUpRight,
  Bell
} from 'lucide-react';
import StatusChip from '../StatusChip';

const ReportDialog = ({ 
  reportDialogOpen, 
  setReportDialogOpen, 
  selectedReport, 
  reportResponse, 
  setReportResponse, 
  handleProcessReport,
  handleViewProperty, // Función para abrir el diálogo de propiedades
  handleViewUser      // Función para ver el usuario que reportó
}) => {
  // Estado para la información de la propiedad relacionada
  const [propertyDetails, setPropertyDetails] = useState(null);
  
  // Estado para historial de respuestas (en caso de que sea impugnado)
  const [responseHistory, setResponseHistory] = useState([]);
  
  // Cargar detalles de la propiedad cuando se abre el diálogo
  useEffect(() => {
    if (reportDialogOpen && selectedReport?.propiedad_id) {
      // En un caso real, aquí cargaríamos más detalles de la propiedad desde una API
      const loadPropertyDetails = async () => {
        try {
          // Intentar obtener los detalles completos de la propiedad desde la API
          const response = await axios.get(`/api/admin/properties/${selectedReport.propiedad_id}`);
          const propData = response.data.property;
          const vendedor = response.data.vendedor;
          
          setPropertyDetails({
            id: selectedReport.propiedad_id,
            titulo: selectedReport.propiedad_titulo || propData?.titulo || `Propiedad ${selectedReport.propiedad_id}`,
            ubicacion: selectedReport.propiedad_ubicacion || propData?.ubicacion || 'Ubicación no disponible',
            estado: propData?.estado || 'activo',
            propietario_nombre: vendedor?.nombre || selectedReport.propietario_nombre || null,
            propietario_email: vendedor?.email || selectedReport.propietario_email || null,
            propietario_id: vendedor?.id || selectedReport.id_vendedor || null
          });
        } catch (error) {
          console.error('Error al cargar detalles de la propiedad:', error);
          // Si falla, usar los datos disponibles en el reporte
          setPropertyDetails({
            id: selectedReport.propiedad_id,
            titulo: selectedReport.propiedad_titulo || `Propiedad ${selectedReport.propiedad_id}`,
            ubicacion: selectedReport.propiedad_ubicacion || 'Ubicación no disponible',
            estado: 'activo',
            propietario_nombre: selectedReport.propietario_nombre || null,
            propietario_email: selectedReport.propietario_email || null,
            propietario_id: selectedReport.id_vendedor || null
          });
        }
      };
      
      loadPropertyDetails();
      
      // En un caso real, cargaríamos el historial de respuestas si existiera
      if (selectedReport.estado === 'impugnado') {
        setResponseHistory([
          {
            id: 1,
            date: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000),
            admin: 'Admin',
            response: 'Reporte rechazado por falta de evidencia',
            action: 'reject'
          }
        ]);
      } else {
        setResponseHistory([]);
      }
    }
  }, [reportDialogOpen, selectedReport]);
  
  // Función para ver la propiedad en el diálogo de propiedades
  const openPropertyDialog = () => {
    if (propertyDetails && handleViewProperty) {
      // Cerrar este diálogo
      setReportDialogOpen(false);
      // Abrir el diálogo de propiedades con la información disponible
      handleViewProperty(propertyDetails);
    }
  };
  
  // Función para ver el usuario que reportó
  const openUserDialog = () => {
    if (selectedReport?.usuario_id && handleViewUser) {
      // Crear un objeto de usuario con la información disponible
      const user = {
        id: selectedReport.usuario_id,
        nombre: selectedReport.usuario_nombre || `Usuario ${selectedReport.usuario_id}`
      };
      
      // Cerrar este diálogo
      setReportDialogOpen(false);
      // Abrir el diálogo de usuario
      handleViewUser(user);
    }
  };
  
  // Función para suspender directamente la propiedad
  const handleSuspendProperty = () => {
    if (propertyDetails && handleViewProperty) {
      // En un caso real, haríamos una llamada a la API para suspender la propiedad
      // Luego actualizaríamos el estado local
      
      // Simular la propiedad actualizada con estado "suspendido"
      const updatedProperty = {
        ...propertyDetails,
        estado: 'suspendido'
      };
      
      // Cerrar este diálogo
      setReportDialogOpen(false);
      // Abrir el diálogo de propiedades con la propiedad actualizada
      handleViewProperty(updatedProperty);
    }
  };
  
  // Obtenemos la traducción del tipo de reporte
  const getReportTypeDisplay = (type) => {
    switch(type) {
      case 'no_disponible': return 'No disponible';
      case 'estafa': return 'Estafa';
      case 'otros': return 'Otros';
      default: return type;
    }
  };
  
  // Determinar la severidad del reporte para mostrar un indicador visual
  const getReportSeverity = (type) => {
    switch(type) {
      case 'estafa': return 'high';
      case 'no_disponible': return 'medium';
      case 'otros': return 'low';
      default: return 'medium';
    }
  };
  
  // Obtener color de severidad para el indicador
  const getSeverityColor = (severity) => {
    switch(severity) {
      case 'high': return 'bg-red-100 text-red-800 border-red-200';
      case 'medium': return 'bg-amber-100 text-amber-800 border-amber-200';
      case 'low': return 'bg-blue-100 text-blue-800 border-blue-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  // Función para gestionar el procesamiento de reportes
  const processReport = async (action) => {
    if (!selectedReport) return;
    
    try {
      // Asegurarse de que propertyDetails existe y tiene los datos necesarios
      if (!propertyDetails) {
        console.error('No hay datos de propiedad disponibles');
        return;
      }
      
      // Crear datos adicionales para enviar al backend - Solo incluir lo que tenemos
      const additionalData = {
        propietario_email: propertyDetails.propietario_email,
        propietario_nombre: propertyDetails.propietario_nombre,
        propiedad_info: {
          id: propertyDetails.id,
          titulo: propertyDetails.titulo,
          ubicacion: propertyDetails.ubicacion
        }
      };
      
      console.log('Enviando datos adicionales para procesar reporte:', additionalData);
      
      // Llamar a la función proporcionada como prop
      await handleProcessReport(action, additionalData);
      
    } catch (error) {
      console.error(`Error en processReport: ${error.message}`);
    }
  };

  return (
    <AnimatePresence>
      {reportDialogOpen && (
        <motion.div
          className="fixed inset-0 overflow-y-auto z-50 flex items-center justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setReportDialogOpen(false)} />
          
          <motion.div
            className="relative bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-screen overflow-hidden"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ type: 'spring', damping: 30 }}
          >
            <div className="px-6 py-4 bg-indigo-600 text-white flex justify-between items-center">
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-medium">
                  Reporte #{selectedReport?.id}
                </h3>
                {selectedReport && (
                  <div className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                    selectedReport.estado === 'pendiente' ? 'bg-red-500/20 text-red-100' :
                    selectedReport.estado === 'impugnado' ? 'bg-amber-500/20 text-amber-100' :
                    selectedReport.estado === 'revisado' ? 'bg-blue-500/20 text-blue-100' :
                    'bg-green-500/20 text-green-100'
                  }`}>
                    {selectedReport.estado}
                  </div>
                )}
              </div>
              <button 
                onClick={() => setReportDialogOpen(false)}
                className="p-1 rounded-full hover:bg-white/10 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {selectedReport && (
              <>
                <div className="p-6 max-h-[80vh] overflow-y-auto">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Información del reporte */}
                    <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-5">
                      <h4 className="text-md font-semibold text-indigo-800 mb-4 flex items-center gap-2">
                        <FileArchive className="w-4 h-4" />
                        Información del Reporte
                      </h4>
                      
                      {/* Tipo de reporte con indicador de severidad */}
                      <div className="mb-4">
                        <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg ${
                          getSeverityColor(getReportSeverity(selectedReport.tipo_reporte))
                        }`}>
                          <AlertTriangle className={`w-4 h-4 ${
                            getReportSeverity(selectedReport.tipo_reporte) === 'high' ? 'text-red-500' :
                            getReportSeverity(selectedReport.tipo_reporte) === 'medium' ? 'text-amber-500' :
                            'text-blue-500'
                          }`} />
                          <span className="text-sm font-medium">
                            {getReportTypeDisplay(selectedReport.tipo_reporte)}
                          </span>
                        </div>
                      </div>
                      
                      <div className="space-y-3">
                        <div>
                          <p className="text-xs font-medium text-gray-500">Estado</p>
                          <div className="mt-1"><StatusChip status={selectedReport.estado} /></div>
                        </div>
                        <div>
                          <p className="text-xs font-medium text-gray-500">Fecha de Reporte</p>
                          <p className="text-sm text-gray-700">{new Date(selectedReport.fecha_reporte).toLocaleString()}</p>
                        </div>
                        <div>
                          <p className="text-xs font-medium text-gray-500">Reportado por</p>
                          <button 
                            onClick={openUserDialog}
                            className="flex items-center gap-1.5 mt-1 text-sm text-indigo-600 hover:text-indigo-800 transition-colors"
                          >
                            <User2Icon className="w-4 h-4" />
                            <span>{selectedReport.usuario_nombre || selectedReport.usuario_id}</span>
                            <ArrowUpRight className="w-3 h-3" />
                          </button>
                        </div>
                        <div>
                          <p className="text-xs font-medium text-gray-500">Descripción</p>
                          <div className="mt-1 bg-gray-50 border border-gray-100 rounded-lg p-3">
                            <p className="text-sm text-gray-700 whitespace-pre-line">
                              {selectedReport.descripcion || 'Sin descripción'}
                            </p>
                          </div>
                        </div>
                      </div>
                      
                      {/* Historial de respuestas en caso de impugnación */}
                      {selectedReport.estado === 'impugnado' && responseHistory.length > 0 && (
                        <div className="mt-5 pt-4 border-t border-gray-100">
                          <h5 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-1.5">
                            <MessageSquare className="w-4 h-4 text-indigo-500" />
                            Respuestas Anteriores
                          </h5>
                          
                          <div className="space-y-3">
                            {responseHistory.map(history => (
                              <div key={history.id} className="bg-indigo-50/50 rounded-lg p-3 border border-indigo-100">
                                <div className="flex justify-between text-xs text-gray-500 mb-1">
                                  <span>{history.admin}</span>
                                  <span>{history.date.toLocaleString()}</span>
                                </div>
                                <p className="text-sm text-gray-700">{history.response}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {/* Información de la propiedad */}
                    <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-5">
                      <h4 className="text-md font-semibold text-indigo-800 mb-4 flex items-center gap-2">
                        <Building className="w-4 h-4" />
                        Información de la Propiedad
                      </h4>
                      
                      {propertyDetails ? (
                        <div className="space-y-3">
                          <div>
                            <p className="text-xs font-medium text-gray-500">ID Propiedad</p>
                            <p className="text-sm text-gray-700">{propertyDetails.id}</p>
                          </div>
                          <div>
                            <p className="text-xs font-medium text-gray-500">Título</p>
                            <p className="text-sm text-gray-700">{propertyDetails.titulo}</p>
                          </div>
                          <div>
                            <p className="text-xs font-medium text-gray-500">Ubicación</p>
                            <p className="text-sm text-gray-700">{propertyDetails.ubicacion}</p>
                          </div>
                          <div>
                            <p className="text-xs font-medium text-gray-500">Estado</p>
                            <div className="mt-1"><StatusChip status={propertyDetails.estado} /></div>
                          </div>
                          <div className="pt-3 mt-3 border-t border-gray-100">
                            <p className="text-xs font-medium text-gray-500 mb-2">Propietario</p>
                            {propertyDetails.propietario_nombre ? (
                              <div className="bg-blue-50 rounded-lg p-2 border border-blue-100">
                                <p className="text-sm text-gray-700"><span className="font-medium">Nombre:</span> <span data-propietario-nombre>{propertyDetails.propietario_nombre}</span></p>
                                <p className="text-sm text-gray-700"><span className="font-medium">Email:</span> <span data-propietario-email>{propertyDetails.propietario_email || 'No disponible'}</span></p>
                              </div>
                            ) : (
                              <p className="text-sm text-amber-600">Información del propietario no disponible</p>
                            )}
                          </div>
                          
                          <div className="pt-4 mt-2 border-t border-gray-100 flex gap-2 flex-wrap">
                            <motion.button 
                              className="px-4 py-2 text-sm text-indigo-600 border border-indigo-200 rounded-lg hover:bg-indigo-50 flex items-center gap-1"
                              whileHover={{ scale: 1.02 }}
                              whileTap={{ scale: 0.98 }}
                              onClick={openPropertyDialog}
                            >
                              <Eye className="w-4 h-4" />
                              Ver Propiedad
                            </motion.button>
                            
                            {selectedReport.estado === 'pendiente' && (
                              <motion.button
                                className="px-4 py-2 text-sm text-amber-600 border border-amber-200 rounded-lg hover:bg-amber-50 flex items-center gap-1"
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                onClick={handleSuspendProperty}
                              >
                                <Ban className="w-4 h-4" />
                                Suspender Propiedad
                              </motion.button>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center justify-center py-10">
                          <AlertTriangle className="w-12 h-12 text-amber-400 mb-2" />
                          <p className="text-gray-600">No se pudo cargar la información de la propiedad</p>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Panel de respuesta para reportes pendientes o impugnados */}
                  {(selectedReport.estado === 'pendiente' || selectedReport.estado === 'impugnado') && (
                    <div className="mt-6 bg-white rounded-lg border border-gray-200 shadow-sm p-5">
                      <h4 className="text-md font-semibold text-indigo-800 mb-3 flex items-center gap-2">
                        <MessageSquare className="w-4 h-4" />
                        {selectedReport.estado === 'impugnado' ? 'Nueva Respuesta' : 'Respuesta'}
                      </h4>
                      
                      <textarea
                        value={reportResponse}
                        onChange={(e) => setReportResponse(e.target.value)}
                        placeholder={
                          selectedReport.estado === 'impugnado' ? 
                          "Escribe una respuesta a la impugnación..." : 
                          "Escribe una respuesta para este reporte..."
                        }
                        className="w-full px-4 py-3 bg-indigo-50/50 border border-indigo-100 rounded-lg text-gray-700 
                                placeholder-indigo-300 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        rows={4}
                      />
                      
                      <div className="mt-3 flex flex-col space-y-2">
                        <p className="text-sm text-gray-600 font-medium">Notificaciones por Email:</p>
                        <div className="flex items-center">
                          <label className="inline-flex items-center text-sm text-gray-600">
                            <input
                              type="checkbox"
                              className="form-checkbox h-4 w-4 text-indigo-600 rounded border-gray-300 focus:ring-indigo-500"
                              checked={true}
                              disabled={true}
                            />
                            <span className="ml-2">Enviar notificación al usuario que reportó: <span className="text-indigo-600 font-medium">{selectedReport?.usuario_email || selectedReport?.usuario_nombre}</span></span>
                          </label>
                        </div>
                        <div className="flex items-center">
                          <label className="inline-flex items-center text-sm text-gray-600">
                            <input
                              type="checkbox"
                              className="form-checkbox h-4 w-4 text-indigo-600 rounded border-gray-300 focus:ring-indigo-500"
                              checked={true}
                              disabled={true}
                            />
                            <span className="ml-2">
                              Enviar notificación al propietario: 
                              <span className="text-indigo-600 font-medium ml-1">
                                {propertyDetails?.propietario_email ? 
                                 propertyDetails.propietario_email : 
                                 propertyDetails?.propietario_nombre ? 
                                 propertyDetails.propietario_nombre + ' (email no disponible)' : 
                                 'Propietario no identificado'}
                              </span>
                            </span>
                          </label>
                        </div>
                        <div className="pt-2 text-xs bg-blue-50 p-2 rounded-lg border border-blue-100">
                          <p className="flex items-center">
                            <span className="bg-blue-500 text-white p-1 rounded-full mr-2">
                              <Bell className="w-3 h-3" />
                            </span>
                            Ambas partes recibirán notificaciones por email con el veredicto y la respuesta que has proporcionado.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                
                <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-3">
                  <motion.button
                    onClick={() => setReportDialogOpen(false)}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    Cerrar
                  </motion.button>
                  
                  {(selectedReport.estado === 'pendiente' || selectedReport.estado === 'impugnado') && (
                    <>
                      <motion.button
                        onClick={() => processReport('reject')}
                        className="px-4 py-2 bg-white border border-red-300 text-red-600 rounded-lg hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        disabled={!reportResponse}
                      >
                        <X className="w-4 h-4" />
                        Rechazar
                      </motion.button>
                      <motion.button
                        onClick={() => processReport('accept')}
                        className="px-4 py-2 bg-gradient-to-r from-indigo-600 to-blue-600 text-white rounded-lg shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        disabled={!reportResponse}
                      >
                        <Check className="w-4 h-4" />
                        Aceptar
                      </motion.button>
                    </>
                  )}
                </div>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ReportDialog;